import axios from "axios";
import config from "../config.js";

let responseInterceptorId = null;
let requestInterceptorId = null;

let authRedirectInProgress = false;
let refreshInFlight = null;
let authLogoutGetDispatch = null;
let authLogoutGetNavigate = null;

const SESSION_REVOKE_PUSH_ACTIONS = new Set(["password_updated"]);

function getAccessToken() {
  try {
    return (
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken") ||
      null
    );
  } catch (_e) {
    return null;
  }
}

function getRefreshToken() {
  try {
    return (
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken") ||
      null
    );
  } catch (_e) {
    return null;
  }
}

function storeTokens(accessToken, refreshToken) {
  const persistInLocal =
    typeof localStorage !== "undefined" &&
    (!!localStorage.getItem("authToken") ||
      !!localStorage.getItem("refreshToken"));
  const target = persistInLocal ? localStorage : sessionStorage;
  const other = persistInLocal ? sessionStorage : localStorage;

  if (accessToken) {
    target.setItem("authToken", accessToken);
    other.removeItem("authToken");
  }
  if (refreshToken) {
    target.setItem("refreshToken", refreshToken);
    other.removeItem("refreshToken");
  }
}

function clearStoredTokens() {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("refreshToken");
  } catch (_e) {
    // ignore
  }
}

function getLoggedInUserId() {
  try {
    const userState = JSON.parse(localStorage.getItem("userState") || "{}");
    if (userState?.user?.id != null) return String(userState.user.id);
  } catch {
    /* ignore */
  }
  return null;
}

/** Clear tokens and redirect to login (401 handler, push session revoke, etc.). */
export function performAuthLogout() {
  if (authRedirectInProgress) {
    return;
  }
  authRedirectInProgress = true;

  const currentPath = typeof window !== "undefined" ? (window.location.pathname + window.location.search) : null;
  if (currentPath && currentPath !== "/" && currentPath !== "/login") {
    try {
      sessionStorage.setItem("redirectUrl", currentPath);
      localStorage.setItem("redirectUrl", currentPath);
    } catch (_e) {}
  }

  clearStoredTokens();

  const dispatch =
    typeof authLogoutGetDispatch === "function" ? authLogoutGetDispatch() : null;
  const navigate =
    typeof authLogoutGetNavigate === "function" ? authLogoutGetNavigate() : null;

  if (typeof dispatch === "function") {
    dispatch({ type: "CLEAR_USER" });
  }

  if (currentPath && currentPath !== "/" && currentPath !== "/login") {
    const redirectLogin = `/login?redirect=${encodeURIComponent(currentPath)}`;
    if (typeof navigate === "function") {
      navigate(redirectLogin, { replace: true });
    } else if (typeof window !== "undefined") {
      window.location.replace(redirectLogin);
    }
  } else {
    if (typeof navigate === "function") {
      navigate("/login", { replace: true });
    } else if (typeof window !== "undefined") {
      window.location.replace("/login");
    }
  }

  window.setTimeout(() => {
    authRedirectInProgress = false;
  }, 1500);
}

export function shouldLogoutForPushAction(action, targetUserId) {
  if (!action || !SESSION_REVOKE_PUSH_ACTIONS.has(action)) {
    return false;
  }
  const currentUserId = getLoggedInUserId();
  if (!currentUserId || targetUserId == null || targetUserId === "") {
    return false;
  }
  return String(targetUserId) === currentUserId;
}

export function handlePushSessionRevoke(payload) {
  const data = payload?.data || {};
  const action = data.action;
  const targetUserId = data.user_id ?? data.userId;
  if (!shouldLogoutForPushAction(action, targetUserId)) {
    return;
  }
  performAuthLogout();
}

function getResolvedRequestUrl(reqConfig) {
  if (!reqConfig) return "";
  if (reqConfig.url?.startsWith("http")) return reqConfig.url;
  const base = reqConfig.baseURL || config.baseURL || "";
  const path = reqConfig.url || "";
  return `${base}${path}`.replace(/([^:]\/)\/+/g, "$1");
}

function isPublicAuthRequestUrl(url) {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  const patterns = [
    "/auth/login",
    "/auth/refresh",
    "/auth/register",
    "/register",
    "/user/forgot/password",
    "/user/reset",
    "/reset-password",
    "/otp-verify",
  ];
  return patterns.some((p) => lower.includes(p));
}

const SESSION_CHECK_MIN_INTERVAL_MS = 30_000;
const SESSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

let lastSessionCheckAt = 0;

const PUBLIC_APP_PATH_PREFIXES = ["/login", "/reset-password"];

function isPublicAppRoute() {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname || "";
  return PUBLIC_APP_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

function hasStoredAuthSession() {
  return Boolean(getAccessToken() || getRefreshToken());
}

/**
 * Proactively validate session with Distributed-Auth (refresh).
 * After password change, auth revokes refresh tokens — refresh fails → logout.
 * Does not depend on push / notification-service.
 */
export async function verifyAuthSession() {
  if (authRedirectInProgress || isPublicAppRoute()) {
    return;
  }
  if (!hasStoredAuthSession()) {
    return;
  }
  if (!getRefreshToken()) {
    return;
  }

  const now = Date.now();
  if (now - lastSessionCheckAt < SESSION_CHECK_MIN_INTERVAL_MS) {
    return;
  }
  lastSessionCheckAt = now;

  try {
    await refreshAccessToken();
  } catch {
    performAuthLogout();
  }
}

async function refreshAccessToken() {
  // Coalesce concurrent refresh attempts into a single in-flight promise so
  // we never fire /auth/refresh more than once per token rotation.
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }

  refreshInFlight = axios
    .post(
      `${config.authBaseURL}/auth/refresh`,
      { refreshToken },
      {
        headers: { "Content-Type": "application/json" },
        skipAuthRedirect: true,
      },
    )
    .then((response) => {
      const raw = response?.data || {};
      const payload = raw.data != null && typeof raw.data === "object" ? raw.data : raw;
      const newAccess = payload.accessToken || payload.token || null;
      const newRefresh = payload.refreshToken || refreshToken;
      if (!newAccess) {
        throw new Error("Refresh response missing accessToken");
      }
      storeTokens(newAccess, newRefresh);
      return newAccess;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export function installAxiosAuthInterceptor(getDispatch, getNavigate) {
  authLogoutGetDispatch = getDispatch;
  authLogoutGetNavigate = getNavigate;

  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }

  requestInterceptorId = axios.interceptors.request.use((reqConfig) => {
    const resolvedUrl = getResolvedRequestUrl(reqConfig);
    if (isPublicAuthRequestUrl(resolvedUrl)) {
      return reqConfig;
    }
    const token = getAccessToken();
    if (token) {
      reqConfig.headers = reqConfig.headers || {};
      if (!reqConfig.headers.Authorization) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
    }
    return reqConfig;
  });

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      if (status !== 401 && status !== 403) {
        return Promise.reject(error);
      }

      const originalRequest = error.config || {};

      if (originalRequest.skipAuthRedirect === true) {
        return Promise.reject(error);
      }

      const requestUrl = getResolvedRequestUrl(originalRequest);
      if (isPublicAuthRequestUrl(requestUrl)) {
        return Promise.reject(error);
      }

      // Try a single refresh + replay before giving up and redirecting.
      if (!originalRequest._retriedAfterRefresh && getRefreshToken()) {
        originalRequest._retriedAfterRefresh = true;
        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (_refreshErr) {
          // fall through to logout
        }
      }

      if (authRedirectInProgress) {
        return Promise.resolve({
          data: [],
          status: 200,
          statusText: "OK",
          headers: {},
          config: originalRequest,
        });
      }

      performAuthLogout();

      return Promise.reject(error);
    },
  );

  return responseInterceptorId;
}

/** Register focus/visibility/poll checks so revoked sessions logout without push. */
export function installAuthSessionWatchers() {
  if (typeof window === "undefined") {
    return () => {};
  }

  const runCheck = () => {
    verifyAuthSession();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      runCheck();
    }
  };

  window.addEventListener("focus", runCheck);
  document.addEventListener("visibilitychange", onVisibilityChange);

  const pollId = window.setInterval(() => {
    if (document.visibilityState === "visible") {
      runCheck();
    }
  }, SESSION_POLL_INTERVAL_MS);

  return () => {
    window.removeEventListener("focus", runCheck);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearInterval(pollId);
  };
}

export function uninstallAxiosAuthInterceptor() {
  authLogoutGetDispatch = null;
  authLogoutGetNavigate = null;

  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
}
